import React from "react";
import { Chip, ColorInput, Divider, Grid, MultiSelect, NativeSelect, NumberInput, Switch, Textarea, TextInput, Text, Box } from "@mantine/core";
import { DateInput, TimePicker } from '@mantine/dates';
import { FormikHandlers, FormikHelpers, FormikState } from "formik";

import classes from './Forms.module.css';

import { Area_Status_Enum, Area_Type_Enum, Booking_Status_Enum, Booking_Type_Enum, Document_Status_Enum, Document_Type_Enum, Guideline_Role_Enum, Guideline_Status_Enum, Invoice_Target_Enum, Rate_Type_Enum, Tenant_Status_Enum, Tenant_Type_Enum, User_Roles_Enum, User_Status_Enum, User_Type_Enum } from "src/state/gql/_types";
import { GenericDictionary } from "src/state/_types";
import { useAppState } from "src/state";
import { usTimezones } from "src/utils/dates";

import { checkIfFieldHasRequiredValidation, enumToOptions, FormConfig } from "src/utils/forms";
import { sanitizeDecimal } from "src/utils/numbers"

import { GenericUserSelect } from "src/components/forms/GenericUserSelect"
import { SupervisorAuditorSelect } from "src/components/forms/SupervisorAuditorSelect"
import { SuperAdminVenueSelect } from "src/components/forms/SuperAdminVenueSelect"
import { SuperAdminTalentSelect } from "src/components/forms/SuperAdminTalentSelect"
import { VenueAreaSelect } from "src/components/forms/VenueAreaSelect";
import { SuperAdminVenueAreaSelect } from "src/components/forms/SuperAdminVenueAreaSelect";
import { RecurringBookingConfig } from "src/components/forms/RecurringBookingConfig";
import { AddThreadRecipients } from "src/components/forms/AddThreadRecipients";
import { DateTimePickerInput } from "src/components/forms/DateTimePickerInput";
import { AssetUpload, TalentAssetUpload } from "src/components/forms/AssetUpload";
import { OperationsAdminTalentSelect } from "./OperationsAdminTalentSelect";
import { OperationsAdminAreaSelect } from "./OperationsAdminAreaSelect";
import { AgencyRoleSelect } from "src/components/forms/AgencyRoleSelect";
import { VenueRoleSelect } from "./VenueRoleSelect";
import { GuidelineDocumentUpload } from "src/components/forms/GuidelineDocumentUpload";
import { GuidelineMetaDataBuilder } from "src/components/forms/GuidelineMetaDataBuilder";
import { GuidelineMetaDataRenderer } from "src/components/forms/GuidelineMetaDataRenderer";
import { formatSnakeCase } from "src/utils/string";
import { Icon } from "@iconify/react/dist/iconify.js";

type FieldRendererProps<PayloadType extends GenericDictionary, T extends GenericDictionary> = {
    formConfig: FormConfig<T>;
    currentItemId?: string | null;
    formMode: 'create' | 'update' | 'bulkUpdate' | null;
    values: FormikState<PayloadType>['values'];
    errors: FormikState<PayloadType>['errors'];
    setFieldValue: FormikHelpers<PayloadType>['setFieldValue'];
    handleBlur: FormikHandlers['handleBlur'];
    handleChange: FormikHandlers['handleChange'];
    checkSubsAreValid?: (valid: boolean) => void;
}

export function FieldRenderer<PayloadType extends GenericDictionary, T extends GenericDictionary>({
    currentItemId = null,
    formConfig,
    formMode,
    values,
    errors,
    setFieldValue,
    handleBlur,
    checkSubsAreValid,
    //handleChange
}: React.PropsWithChildren<FieldRendererProps<PayloadType, T>>): React.ReactElement | null {

    const profile = useAppState(state => state.profile)
    //const isMobile = useMediaQuery('(max-width: 1024px)');

    const tenantTypeOptions = React.useMemo(
        () => enumToOptions(Tenant_Type_Enum, ['agency']),
        []
    );

    const venueUserRoleOptions = React.useMemo(
        () => enumToOptions(User_Roles_Enum, ['superadmin']),
        []
    );

    const areaTypeOptions = React.useMemo(
        () => enumToOptions(Area_Type_Enum),
        []
    );

    const areaStatusOptions = React.useMemo(
        () => enumToOptions(Area_Status_Enum),
        []
    );

    const guidelineStatusOptions = React.useMemo(
        () => enumToOptions(Guideline_Status_Enum),
        []
    );

    const guidelineRoleOptions = React.useMemo(
        () => enumToOptions(Guideline_Role_Enum),
        []
    );

    const userStatusOptions = React.useMemo(
        () => enumToOptions(User_Status_Enum),
        []
    );

    const rateTypeOptions = React.useMemo(
        () => enumToOptions(Rate_Type_Enum),
        []
    );

    const invoiceTargetOptions = React.useMemo(
        () => enumToOptions(Invoice_Target_Enum),
        []
    );

    const bookingTypeOptions = React.useMemo(
        () => enumToOptions(Booking_Type_Enum),
        []
    );

    const documentStatusOptions = React.useMemo(
        () => enumToOptions(Document_Status_Enum),
        []
    );

    const documentTypeOptions = React.useMemo(
        () => enumToOptions(Document_Type_Enum),
        []
    );

    const timezoneOptions = React.useMemo(
        () => [
            ...usTimezones
        ],
        []
    );

    return (
        <React.Fragment>

            {Object.keys(formConfig.fields).map((fieldId, key) => {

                let fieldSchema = formConfig.fields[fieldId];
                let formFieldId = fieldId as keyof PayloadType;
                let isFieldRequired = checkIfFieldHasRequiredValidation(fieldSchema);
                let isFieldEditable = formMode === 'create' ? true : formConfig.editable.indexOf(fieldId) > -1;
                let hideLabel = fieldSchema.hideLabel
                let skipRender = fieldSchema.skipRender

                const renderInput = () => {

                    switch (fieldSchema.type) {

                        case 'boolean':

                            return (
                                <Switch
                                    label={fieldSchema.label}
                                    size="md"
                                    name={formFieldId as string}
                                    onChange={(e) => {
                                        setFieldValue(formFieldId as string, !!e.currentTarget.checked)
                                    }}
                                    checked={values[formFieldId]}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    error={errors[formFieldId] as string}
                                    mb="xs"
                                    onLabel={<Icon icon="mdi:check" width={12} />}
                                    color={values[formFieldId] ? 'green.9' : 'red'}
                                    styles={!!errors[formFieldId] ? { track: { border: '1px solid var(--mantine-color-red-9)' } }: { }}
                                />
                            );

                        case 'toggle':
                            return (
                                <Chip
                                    size="md"
                                    radius="sm"
                                    checked={values[formFieldId]}
                                    onChange={() => {
                                        setFieldValue(formFieldId as string, !values[formFieldId])
                                    }}
                                    mb={20}
                                >
                                    {fieldSchema.label}
                                </Chip>
                            );

                        case 'number':
                            return (
                                <NumberInput
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(value) => {
                                        setFieldValue(formFieldId as string, sanitizeDecimal(value as number))
                                    }}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    defaultValue={0}
                                    allowDecimal
                                    step={1}
                                    stepHoldDelay={500}
                                    stepHoldInterval={100}
                                    onBlur={handleBlur}
                                    classNames={classes}
                                    onFocus={(event) => event.target.select()}
                                />
                            );

                        case 'currency':
                            return (
                                <NumberInput
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(value) => {
                                        setFieldValue(formFieldId as string, sanitizeDecimal(value as number))
                                    }}
                                    value={values[formFieldId] === '' ? '' : values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    prefix="$"
                                    decimalScale={2}
                                    fixedDecimalScale
                                    allowDecimal
                                    allowNegative={false}
                                    thousandSeparator=","
                                    step={1}
                                    stepHoldDelay={500}
                                    stepHoldInterval={100}
                                    onBlur={handleBlur}
                                    classNames={classes}
                                    onFocus={(event) => event.target.select()}
                                />
                            );

                        case 'tenant-role-select':
                            let isSameUserDisabled = (formMode === 'update' || formMode === 'bulkUpdate') && currentItemId === profile?.id
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select...', value: '' },
                                        { label: 'Admin', value: User_Roles_Enum.Administrator },
                                        { label: 'User', value: User_Roles_Enum.User }
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable || isSameUserDisabled}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'objective-condition-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select...', value: '' },
                                        { label: 'Number: Is Greater Than (>)', value: 'greaterThan' },
                                        { label: 'Number: Is Less Than (<)', value: 'lessThan' },
                                        { label: 'Number: Is Equal To (<)', value: 'isEqual' },
                                        { label: 'Text: Contains', value: 'contains' },
                                        { label: 'YesOrNo: Equals', value: 'yesOrNo' },
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'admin-role-select':
                            let isSameUserDisabled2 = (formMode === 'update' || formMode === 'bulkUpdate') && currentItemId === profile?.id
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select...', value: '' },
                                        { label: 'Superadmin', value: User_Roles_Enum.Superadmin }
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable || isSameUserDisabled2}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'tenant-status-enum':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Status...', value: '' },
                                        ...(Object.keys(Tenant_Status_Enum) as Array<keyof typeof Tenant_Status_Enum>).map((key) => {
                                            return { label: key, value: Tenant_Status_Enum[key] }
                                        })
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'superadmin-booking-status-enum':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Status...', value: '' },
                                        ...(Object.keys(Booking_Status_Enum) as Array<keyof typeof Booking_Status_Enum>).map((key) => {
                                            return { label: key, value: Booking_Status_Enum[key] }
                                        })
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'booking-status-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'All Stages', value: '' },
                                        ...(Object.keys(Booking_Status_Enum) as Array<keyof typeof Booking_Status_Enum>).map((key) => {
                                            return { label: key, value: Booking_Status_Enum[key] }
                                        })
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value || null)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId] ?? ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'booking-type-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'All Types', value: '' },
                                        ...(Object.keys(Booking_Type_Enum) as Array<keyof typeof Booking_Type_Enum>).map((key) => {
                                            return { label: key, value: Booking_Type_Enum[key] }
                                        })
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value || null)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId] ?? ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'booking-rate-type-enum':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Rate Type...', value: '' },
                                        ...rateTypeOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'booking-invoice-target-enum':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Invoice Target...', value: '' },
                                        ...invoiceTargetOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'booking-type-enum':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Booking Type...', value: '' },
                                        ...bookingTypeOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'superadmin-user-type-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select User Type...', value: '' },
                                        ...(Object.keys(User_Type_Enum) as Array<keyof typeof User_Type_Enum>).map((key) => {
                                            return { label: key, value: User_Type_Enum[key] }
                                        })
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'superadmin-user-role-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select User Role...', value: '' },
                                        ...(Object.keys(User_Roles_Enum) as Array<keyof typeof User_Roles_Enum>).map((key) => {
                                            return { label: key, value: User_Roles_Enum[key] }
                                        })
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'venue-user-role-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select User Role...', value: '' },
                                        ...venueUserRoleOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'agency-role-select':
                            return (
                                <AgencyRoleSelect
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    label={fieldSchema.label}
                                    onChange={(v) => setFieldValue(formFieldId as string, v)}
                                    onBlur={handleBlur}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                    getValues={{ type: values['type'] || '' }}
                                />
                            );

                        case 'venue-role-select':
                            return (
                                <VenueRoleSelect
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    label={fieldSchema.label}
                                    onChange={(v) => setFieldValue(formFieldId as string, v)}
                                    onBlur={handleBlur}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                    getValues={{ type: values['type'] || '' }}
                                />
                            );

                        case 'area-type-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Area Type...', value: '' },
                                        ...areaTypeOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'area-status-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Status...', value: '' },
                                        ...areaStatusOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'document-status-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Status...', value: '' },
                                        ...documentStatusOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'document-type-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Type...', value: '' },
                                        ...documentTypeOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'guideline-status-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Status...', value: '' },
                                        ...guidelineStatusOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'guideline-role-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Role...', value: '' },
                                        ...guidelineRoleOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'user-status-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Status...', value: '' },
                                        ...userStatusOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'timezone-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Timezone...', value: '' },
                                        ...timezoneOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'tenant-type-enum':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Type...', value: '' },
                                        ...tenantTypeOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'venue-type-enum':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Type...', value: '' },
                                        ...tenantTypeOptions
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'email':
                            return (
                                <TextInput
                                    type="email"
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue(formFieldId as string, e.target.value)}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'textarea':
                            return (
                                <Textarea
                                    label={!hideLabel ? fieldSchema.label : ''}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                        setFieldValue(formFieldId as string, e.currentTarget.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    autosize
                                    minRows={2}
                                    maxRows={5}
                                    classNames={classes}
                                />
                            );

                        case 'color-picker':
                            return (
                                <ColorInput
                                    label={!hideLabel ? fieldSchema.label : ''}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(value) => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );
                        case 'message-textarea':
                            return (
                                <Textarea
                                    label={!hideLabel ? fieldSchema.label : ''}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                        setFieldValue(formFieldId as string, e.currentTarget.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    placeholder="New message text"
                                    autosize
                                    minRows={3}
                                    styles={{
                                        input: {
                                            background: 'transparent'
                                        }
                                    }}
                                    classNames={classes}
                                />
                            );

                        case 'generic-user-select':
                            return (
                                <GenericUserSelect
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );
                        case 'supervisor-auditor-select':
                            return (
                                <SupervisorAuditorSelect
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'superadmin-venue-area-select':
                            return (
                                <SuperAdminVenueAreaSelect
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                    getValues={{
                                        venue_id: values['venue_id'] || ''
                                    }}
                                />
                            );

                        case 'administrator-venue-area-select':
                            return (
                                <OperationsAdminAreaSelect
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                    getValues={{
                                        venue_id: values['venue_id'] || ''
                                    }}
                                />
                            );

                        case 'superadmin-booking-venue-select':
                            return (
                                <SuperAdminVenueSelect
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'venue-area-select':
                            return (
                                <VenueAreaSelect
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'superadmin-booking-talent-select':
                            return (
                                <SuperAdminTalentSelect
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'administrator-booking-talent-select':
                            return (
                                <OperationsAdminTalentSelect
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'venue-guideline-title-select':
                            return (
                                <NativeSelect
                                    id={formFieldId as string}
                                    label={fieldSchema.label}
                                    data={[
                                        { label: 'Select Guideline Type...', value: '' },
                                        ...[
                                            "Terms",
                                            "Policies",
                                            "Open/Close Times",
                                            "Capacity",
                                            "Music Direction/Notes",
                                            "Volume Details",
                                            "Other Notes",
                                            "Parking",
                                            "Policies",
                                            "Comp Tab Details"
                                        ].map(i => ({ label: i, value: i }))
                                    ]}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'date-picker':
                            let isSet1 = typeof values[formFieldId] === 'string' && !!values[formFieldId].length
                            let rawDate1 = isSet1 ? new Date(Date.parse(values[formFieldId])) : new Date()
                            rawDate1.setMinutes(rawDate1.getMinutes() + rawDate1.getTimezoneOffset())
                            let dateValue1 = isSet1 ? typeof values[formFieldId] === 'object' ? values[formFieldId] : rawDate1 : null
                            return (
                                <DateInput
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(value) => {
                                        if (typeof value === 'string') {
                                            setFieldValue(formFieldId as string, value)
                                        }
                                        if (value === null) {
                                            setFieldValue(formFieldId as string, '')
                                        }
                                        if (typeof value === 'object' && value !== null) {
                                            let dobj = value as any
                                            setFieldValue(formFieldId as string, dobj.toISOString())
                                        }
                                    }}
                                    clearable
                                    onBlur={handleBlur}
                                    value={dateValue1}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                    dateParser={(dateString) => new Date(Date.parse(dateString))}
                                />
                            );

                        case 'time-picker':
                            return (
                                <TimePicker
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(value) => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId]}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                    withDropdown
                                    format="12h"
                                />
                            );

                        case 'date-time-picker':
                            return (
                                <DateTimePickerInput
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'multi-select':
                            let multiSelectValue = !!values[formFieldId] && typeof values[formFieldId] == 'string' && values[formFieldId].length ? values[formFieldId].split(',') : []
                            return (
                                <MultiSelect
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    data={[
                                        "Pop",
                                        "Hip-Hop/Rap",
                                        "R&B/Soul",
                                        "Rock",
                                        "Country",
                                        "Latin/Reggaeton",
                                        "Electronic/Dance (EDM)",
                                        "Alternative/Indie",
                                        "Funk",
                                        "Jazz",
                                        "Blues",
                                        "Gospel/Christian",
                                        "Metal",
                                        "Punk",
                                        "Folk/Americana",
                                        "Reggae",
                                        "Afrobeats",
                                        "K-Pop",
                                        "Lo-Fi/Chillhop",
                                        "House/Techno"
                                    ]}
                                    onChange={(value) => {
                                        setFieldValue(formFieldId as string, value.join(','))
                                    }}
                                    onBlur={handleBlur}
                                    value={multiSelectValue}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    clearable
                                    searchable
                                    hidePickedOptions
                                    classNames={classes}
                                />
                            );

                        case 'add-thread-recipients':
                            return (
                                <AddThreadRecipients
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(value) => {
                                        if (value) {
                                            setFieldValue(formFieldId as string, value)
                                        }
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'recurring-booking-config':
                            return (
                                <RecurringBookingConfig
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                    getValues={{
                                        date: values['date'] || ''
                                    }}
                                    setValues={setFieldValue}
                                    checkSubsAreValid={checkSubsAreValid}
                                />
                            );

                        case 'guideline-metadata-builder':
                            return (
                                <GuidelineMetaDataBuilder
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'guideline-metadata-renderer':
                            return (
                                <GuidelineMetaDataRenderer
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'asset-upload':
                            return (
                                <React.Fragment>
                                    <AssetUpload
                                        schemaId={formConfig.id}
                                        parentId={currentItemId}
                                        type={formFieldId as string}
                                        label={fieldSchema.label}
                                        id={formFieldId as string}
                                        name={formFieldId as string}
                                        onChange={value => {
                                            setFieldValue(formFieldId as string, value)
                                        }}
                                        value={values[formFieldId] || ''}
                                        error={errors[formFieldId] as string}
                                        required={isFieldRequired}
                                        disabled={!isFieldEditable}
                                        description={fieldSchema.help}
                                        classNames={classes}
                                        setValues={setFieldValue}
                                        setValueKeys={['filename', 'size', 'filetype']}
                                        getValues={{
                                            id: values['id'] || ''
                                        }}
                                    />
                                </React.Fragment>
                            );

                        case 'talent-asset-upload':
                            return (
                                <React.Fragment>
                                    <TalentAssetUpload
                                        schemaId={formConfig.id}
                                        parentId={currentItemId}
                                        type={formFieldId as string}
                                        label={fieldSchema.label}
                                        id={formFieldId as string}
                                        name={formFieldId as string}
                                        onChange={value => {
                                            setFieldValue(formFieldId as string, value)
                                        }}
                                        value={values[formFieldId] || ''}
                                        error={errors[formFieldId] as string}
                                        required={isFieldRequired}
                                        disabled={!isFieldEditable}
                                        description={fieldSchema.help}
                                        classNames={classes}
                                        getValues={{
                                            type: values['type'] || ''
                                        }}
                                    />
                                </React.Fragment>
                            );

                        case 'guideline-document-upload':
                            return (
                                <GuidelineDocumentUpload
                                    schemaId={formConfig.id}
                                    parentId={currentItemId}
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={value => {
                                        setFieldValue(formFieldId as string, value)
                                    }}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                        case 'text-display':
                            return (
                                <Box mb="xs">
                                    <Text size="sm" lh={1.2} fw={500}>{fieldSchema.label}</Text>
                                    <Text size="md" c="dimmed" lh={1.2}>{formatSnakeCase(values[formFieldId] || '')}</Text>
                                </Box>
                            );

                        default:
                            return (
                                <TextInput
                                    type="text"
                                    label={fieldSchema.label}
                                    id={formFieldId as string}
                                    name={formFieldId as string}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setFieldValue(formFieldId as string, e.target.value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values[formFieldId] || ''}
                                    error={errors[formFieldId] as string}
                                    required={isFieldRequired}
                                    disabled={!isFieldEditable}
                                    description={fieldSchema.help}
                                    classNames={classes}
                                />
                            );

                    }

                }

                let gridWidth = fieldSchema.grid ? fieldSchema.grid : 12;

                if (skipRender) {
                    return null
                }

                return (
                    <React.Fragment key={`${formMode}-${formConfig.id}-field-${key}`}>
                        <Grid.Col span={gridWidth}>
                            {renderInput()}
                        </Grid.Col>
                        {!!fieldSchema.divider && (
                            <Grid.Col span={12}>
                                <Divider mb="sm" variant="dotted" />
                            </Grid.Col>
                        )}
                    </React.Fragment>
                )

            })}

        </React.Fragment>
    )

}